/**
 * Ordo team color palette — 32 colors × 3 shades.
 * - light:   tag background
 * - primary: standalone icon on white or dark
 * - dark:    tag text on light background
 *
 * App-aligned families use the live Multipass app registry branding verified
 * on 2026-10-15: Slate (HQ/auth/reporting), Stone (Factory), Aqua (Agent),
 * Blue (Tasks), Rose (Customers), Cobalt (RFPs), Sage (Leads),
 * Violet (Comms), Orange (Schedule), Green (Menus).
 * All 32 families use the brighter, cleaner 2026-10-17 palette. The ten
 * approved app-facing primary shades are retained; all light/dark shades
 * are coordinated with their primary while preserving readable tag text.
 * Amber is the approved gold revision of HubSpot branding; saved app values
 * are managed separately. Curated dark-on-light pairs meet 4.5:1 contrast.
 * These are shared palette defaults, not a runtime mirror of registry edits.
 *
 * Keep the original family order stable when tuning shades. It was initially
 * ordered for perceptual separation; changing it would change team assignment.
 *
 * Color assignment: `colorForTeamIndex(n)` — used in `TeamManager.create`
 * with `n = team.count()` (NOT filtered by `active`, so soft-deleted
 * teams continue to occupy their slot — palette positions are stable
 * across the team's lifecycle).
 */

export interface TeamColor {
  name: string
  light: string
  primary: string
  dark: string
}

export const TEAM_COLORS: readonly TeamColor[] = [
  { name: 'Lemon', light: '#FCFBED', primary: '#E3CF35', dark: '#786E1C' },
  { name: 'Cobalt', light: '#F2F3FC', primary: '#6A79E0', dark: '#384077' },
  { name: 'Aqua', light: '#EAF7F8', primary: '#1BA5B4', dark: '#0E575F' },
  { name: 'Wine', light: '#F8EFF2', primary: '#B34E73', dark: '#5F293D' },
  { name: 'Green', light: '#EEF7F2', primary: '#43AA73', dark: '#245A3D' },
  { name: 'Khaki', light: '#F8F7F2', primary: '#B0A171', dark: '#5D553C' },
  { name: 'Coral', light: '#FEF4F4', primary: '#F08089', dark: '#7F4449' },
  { name: 'Plum', light: '#F7F0F7', primary: '#A85DA5', dark: '#593157' },
  { name: 'Fuchsia', light: '#FAEEFA', primary: '#CC40C5', dark: '#6C2268' },
  { name: 'Lavender', light: '#F8F9FE', primary: '#B6B9F2', dark: '#606280' },
  { name: 'Slate', light: '#EFF1F3', primary: '#52647C', dark: '#2B3542' },
  { name: 'Mint', light: '#F2FAF8', primary: '#72C9AC', dark: '#3C6B5B' },
  { name: 'Rose', light: '#FCF2F7', primary: '#E16FA5', dark: '#773B57' },
  { name: 'Amber', light: '#FCF7EB', primary: '#DEA923', dark: '#765A13' },
  { name: 'Lime', light: '#F8FAEC', primary: '#B0C82F', dark: '#5D6A19' },
  { name: 'Cinnamon', light: '#F7F1ED', primary: '#A96332', dark: '#5A341A' },
  { name: 'Purple', light: '#F3EDFA', primary: '#7C38C4', dark: '#421E68' },
  { name: 'Sky', light: '#F1F8FC', primary: '#62B3E1', dark: '#345F77' },
  { name: 'Peach', light: '#FEF8F3', primary: '#F7B276', dark: '#835E3F' },
  { name: 'Forest', light: '#ECF4EF', primary: '#2E8052', dark: '#18442B' },
  { name: 'Mustard', light: '#F8F4EB', primary: '#AF8622', dark: '#5D4712' },
  { name: 'Blue', light: '#EEF5FE', primary: '#428FF0', dark: '#234C7F' },
  { name: 'Olive', light: '#F4F5EE', primary: '#8A9140', dark: '#494D22' },
  { name: 'Stone', light: '#F1F1F0', primary: '#68635C', dark: '#373431' },
  { name: 'Violet', light: '#F6F3FB', primary: '#9B78D5', dark: '#524071' },
  { name: 'Terracotta', light: '#FCF2EF', primary: '#DD694B', dark: '#753828' },
  { name: 'Red', light: '#FDEFF0', primary: '#EA505D', dark: '#7C2A31' },
  { name: 'Teal', light: '#EBF7F5', primary: '#20AA91', dark: '#115A4D' },
  { name: 'Orange', light: '#FDF5EF', primary: '#EB9151', dark: '#7D4D2B' },
  { name: 'Sage', light: '#F4F8EF', primary: '#84AD4E', dark: '#465C29' },
  { name: 'Taupe', light: '#F7F6F5', primary: '#A39A8C', dark: '#56524A' },
  { name: 'Charcoal', light: '#F4F5F6', primary: '#818B99', dark: '#444A51' },
] as const

/** Hue-grouped picker order; never use this for automatic team assignment. */
export const TEAM_COLOR_OPTIONS: readonly TeamColor[] = [
  'Red',
  'Coral',
  'Rose',
  'Fuchsia',
  'Wine',
  'Plum',
  'Purple',
  'Violet',
  'Lavender',
  'Cobalt',
  'Blue',
  'Sky',
  'Aqua',
  'Teal',
  'Mint',
  'Green',
  'Forest',
  'Sage',
  'Lime',
  'Olive',
  'Lemon',
  'Amber',
  'Mustard',
  'Peach',
  'Orange',
  'Terracotta',
  'Cinnamon',
  'Khaki',
  'Taupe',
  'Stone',
  'Slate',
  'Charcoal',
].map((name) => TEAM_COLORS.find((color) => color.name === name)!)

/**
 * Resolve the assigned color for the n-th team ever created (including
 * soft-deleted ones). Wraps modulo the palette size, so team #33 reuses
 * the same color as team #1. Negative indices are tolerated.
 */
export function colorForTeamIndex(index: number): TeamColor {
  const n = TEAM_COLORS.length
  return TEAM_COLORS[((index % n) + n) % n]
}

/** Look up a palette entry by `name` (case-sensitive). Undefined for unknown names. */
export function findTeamColor(name: string): TeamColor | undefined {
  return TEAM_COLORS.find((c) => c.name === name)
}

/**
 * Resolve a stored color name to the full triple. Falls back to the
 * first palette entry if the name isn't recognized — defensive against
 * legacy/manual rows; assigned colors always round-trip cleanly.
 */
export function resolveTeamColor(name: string): TeamColor {
  if (/^#[0-9a-fA-F]{6}$/.test(name)) {
    const primary = name.toUpperCase()
    // Existing HQ clients save hex. Preserve curated family shades and the
    // stored value when that hex is a palette color.
    const family = TEAM_COLORS.find((color) => color.primary === primary)
    if (family) {
      return { ...family, name: primary }
    }
    const channels = [1, 3, 5].map((offset) => parseInt(primary.slice(offset, offset + 2), 16))
    const mix = (target: number, amount: number) =>
      '#' +
      channels
        .map((channel) =>
          Math.round(channel * (1 - amount) + target * amount)
            .toString(16)
            .padStart(2, '0'),
        )
        .join('')
        .toUpperCase()
    // Keep the existing structured wire contract for all consuming apps.
    return { name: primary, primary, light: mix(255, 0.88), dark: mix(0, 0.35) }
  }
  return findTeamColor(name) ?? TEAM_COLORS[0]
}

const LEGACY_TEAM_COLOR_NAMES: Record<string, string> = {
  yellow: 'Lemon',
  emerald: 'Mint',
  cyan: 'Aqua',
  indigo: 'Cobalt',
  pink: 'Rose',
}

/** Accept canonical names, old UI keys, and family hex values. */
export function findTeamColorValue(value: string): TeamColor | undefined {
  const normalized = value.trim().toLowerCase()
  const alias = LEGACY_TEAM_COLOR_NAMES[normalized]
  return TEAM_COLORS.find(
    (color) =>
      color.name.toLowerCase() === normalized || color.primary.toLowerCase() === normalized || color.name === alias,
  )
}

/** UI fallback is Slate; Multipass's historical assignment fallback stays Lemon. */
export function getTeamColor(value: string): TeamColor {
  const family = findTeamColorValue(value)
  if (family) {
    return family
  }
  if (/^#[0-9a-fA-F]{6}$/.test(value.trim())) {
    return resolveTeamColor(value.trim())
  }
  return findTeamColor('Slate')!
}
